import { v4 as uuidv4 } from 'uuid'

import { readTam } from '@aytm/app/stats/page/question-views/vk/lib/utils'

const transformUnitItemsToClient = data =>
  (data || []).map(({ value, quantity }) => {
    return { id: uuidv4(), value: (value / 100).toFixed(2), quantity }
  })

// A row is carried only once BOTH halves are filled. An unfilled box is not a zero: coercing it
// wrote the row anyway with the missing half as 0, so a half-finished row joined the persisted grid
// and the computations, and came back on the next read as a real one. It stays in the editor either
// way -- this is about what the grid persists, not about what the researcher can still type into.
// A figure may be typed with thousands separators, as master's masked field allowed; only the
// commas are dropped, so an entry that holds no number is still refused rather than read from its digits.
const withoutSeparators = value => String(value ?? '').replace(/,/g, '')

const isFilled = field => '' !== String(field ?? '').trim() && Number.isFinite(Number(withoutSeparators(field)))

const transformUnitItemsToServer = data =>
  data
    .filter(({ value, quantity }) => isFilled(value) && isFilled(quantity))
    .map(({ value, quantity }) => {
      return { value: Math.ceil(Number(withoutSeparators(value)) * 100), quantity: Number(withoutSeparators(quantity)) }
    })

export const removeUnitItem = (data, id) => data.filter(item => item.id !== id)

export const getDefaultForm = ({ answer, config }) => {
  return {
    price: {
      isTouched: false,
      value: (answer?.clientsPrice?.[0] / 100).toFixed(2)
    },
    cost: {
      isTouched: false,
      value: transformUnitItemsToClient(config?.unit)
    },
    incr: {
      isTouched: false,
      value: config?.increment
    },
    tam: {
      isTouched: false,
      value: answer.TAM?.[0] || 1000
    }
  }
}

export const getSubmitResolvers = ({ config, onModelChange }) => {
  // Nothing to commit against until the config has been derived from a real answer: every field a
  // resolver would carry forward is missing, so a commit made in that window would persist a config
  // the reader never saw. It closes the moment the answer arrives and the block seeds again.
  const seeded = Array.isArray(config?.update)

  const changeModelWithLoading = data => {
    if (!seeded) return

    onModelChange({
      loading: true,
      config: {
        ...config,
        ...data
      }
    })
  }
  return {
    // Coerce before the write, the way the infographic's own TAM input already does: a non-numeric
    // entry must not reach the section, where it persists unusable and takes the chart and all
    // three configuration panels with it. An entry that is not a number leaves the value alone.
    price: value => {
      const numeric = parseFloat(withoutSeparators(value))
      if (!Number.isFinite(numeric)) return

      // whole cents, rounded: the dollar figure times 100 is not exact in binary, and cutting it loses a cent
      const multipliedPrice = Math.round(numeric * 100)

      if (config.price !== multipliedPrice) {
        changeModelWithLoading({
          price: multipliedPrice,
          update: [...config.update, 'price']
        })
      }
    },
    cost: value => {
      changeModelWithLoading({
        unit: transformUnitItemsToServer(value),
        update: [...config.update, 'cost']
      })
    },
    tam: value => {
      const clamped = readTam(value)

      if (config.tam !== clamped) {
        changeModelWithLoading({
          tam: clamped,
          update: [...config.update, 'tam']
        })
      }
    },
    incr: value => {
      changeModelWithLoading({
        increment: value,
        update: [...config.update, 'cost', 'incr']
      })
    }
  }
}
